/**
 * Operator / Observer Login — /observe?key=<token>
 *
 * Secure magic-link landing page that lets a privileged operator log in AS
 * an AI-created trial account, WITHOUT knowing the AI's password. Use case:
 * verifying the synthetic data the AI agent entered after it ran the full
 * /subscribe + /checkout simulation.
 *
 * The link itself is the only credential — the backend signs it with a
 * different salt from the customer-facing /subscribe code, so a leaked
 * customer link cannot impersonate an operator.
 *
 * Industry design system (auth grammar): Z plate + wordmark over a centered
 * blueprint card on the light ground. "Observer login" renders as an
 * ink-bordered grammar chip (not a colored MUI chip); state notes are
 * ink/accent grammar boxes; the session facts are a hairline block with
 * uppercase mini-labels and heading-font values.
 */

import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import apiClient from '../services/api';
import authService from '../services/auth';

const LOCAL_BACKEND = 'http://127.0.0.1:8000/api';

const HEADING = 'var(--font-heading)';
const INK = 'var(--color-neutral-900)';
const ACC = 'var(--color-accent)';
const ACC7 = 'var(--color-accent-700)';
const ACC8 = 'var(--color-accent-800)';
const MUTED58 = 'color-mix(in srgb, var(--color-text) 58%, transparent)';
const MUTED72 = 'color-mix(in srgb, var(--color-text) 72%, transparent)';

const primaryBtnSx = {
  width: '100%',
  minHeight: 44,
  border: `1px solid ${ACC}`,
  background: ACC,
  color: 'var(--color-bg)',
  fontFamily: HEADING,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  borderRadius: 0,
  cursor: 'pointer',
  '&:hover': { background: 'var(--color-accent-600)', borderColor: 'var(--color-accent-600)' },
  '&:active': { background: ACC7 },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
};

const secondaryLinkSx = {
  flex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 40,
  px: '14px',
  border: '1px solid var(--color-divider)',
  color: 'var(--color-text)',
  fontFamily: HEADING,
  fontSize: '11.5px',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  textDecoration: 'none',
  '&:hover': { borderColor: 'rgba(29,31,32,0.45)' },
};

async function callObserverLogin(key) {
  const body = { key };
  const opts = {
    __skipAuthHeader: true,
    __skipAuthRefresh: true,
    __skipLocalRedirect: true,
    timeout: 20000,
  };
  try {
    const { data } = await apiClient.post('/landing/public-trial/observer-login/', body, opts);
    return data;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 400 || status === 404 || status === 500) {
      const { data } = await axios.post(
        `${LOCAL_BACKEND}/landing/public-trial/observer-login/`,
        body,
        { headers: { 'Content-Type': 'application/json' }, timeout: 20000 }
      );
      return data;
    }
    throw err;
  }
}

function persistObserverSession(data) {
  const access = data?.tokens?.access;
  const refresh = data?.tokens?.refresh;
  if (!access || !refresh) {
    throw new Error('Observer login response is missing JWT tokens.');
  }
  // Wipe any existing session before persisting the operator-impersonation
  // tokens so we never silently mix two identities.
  authService.logout();
  localStorage.setItem('access_token', access);
  localStorage.setItem('refresh_token', refresh);
  try {
    const decoded = jwtDecode(access);
    if (decoded) {
      localStorage.setItem('user', JSON.stringify(decoded));
    }
  } catch {
    /* token contents are opaque to us — non-fatal */
  }
  if (data?.user) {
    localStorage.setItem('user_profile', JSON.stringify(data.user));
  }
  if (typeof apiClient.rehydrateSyncCache === 'function') {
    apiClient.rehydrateSyncCache();
  }
  // Surface a small audit breadcrumb so the operator can spot when they're
  // navigating an impersonation session vs. their own account.
  try {
    localStorage.setItem(
      'aviatto_observer_session',
      JSON.stringify({
        user_id: data?.user?.id,
        email: data?.user?.email,
        company: data?.user?.company_name,
        observer_expires_at: data?.observer_token?.expires_at,
        trial_expires_at: data?.trial?.expires_at,
        opened_at: new Date().toISOString(),
      })
    );
  } catch {
    /* localStorage may be unavailable — non-fatal */
  }
}

export default function PublicTrialObserve() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const key = (params.get('key') || params.get('token') || '').trim();

  const [stage, setStage] = useState(key ? 'loading' : 'missing-key');
  const [errorMessage, setErrorMessage] = useState('');
  const [session, setSession] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!key) {
      setStage('missing-key');
      return undefined;
    }
    if (startedRef.current) return undefined;
    startedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const data = await callObserverLogin(key);
        if (cancelled) return;
        persistObserverSession(data);
        setSession(data);
        setStage('success');
      } catch (err) {
        if (cancelled) return;
        const data = err?.response?.data || {};
        setErrorMessage(
          data.error ||
            data.detail ||
            'Nao foi possivel abrir a sessao do operador. Verifique o link ou solicite um novo.'
        );
        setStage('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [key]);

  const handleEnterApp = () => {
    navigate('/home', { replace: true });
  };

  const sessionFacts = session
    ? [
        { k: 'Empresa', v: session.user?.company_name || '-' },
        {
          k: 'Trial expira em',
          v: session.trial?.expires_at
            ? new Date(session.trial.expires_at).toLocaleString('pt-BR')
            : '-',
        },
        {
          k: 'Sua sessao expira em',
          v: session.observer_token?.expires_at
            ? new Date(session.observer_token.expires_at).toLocaleString('pt-BR')
            : '-',
        },
      ]
    : [];

  return (
    <Box
      className="zb-industry"
      sx={{
        minHeight: '100dvh',
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-body)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: '40px 24px',
        boxSizing: 'border-box',
      }}
    >
      {/* Z plate + wordmark */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
        <Box component="span" sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, border: '1.5px solid var(--color-text)', fontFamily: HEADING, fontSize: 15, fontWeight: 600 }}>
          Z
        </Box>
        <Box component="span" sx={{ fontFamily: HEADING, fontSize: 19, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Zulbot
        </Box>
      </Box>

      <Box
        className="blueprint"
        sx={{ width: '100%', maxWidth: 560, mt: 3, p: '26px', background: 'transparent', boxShadow: 'var(--ind-shadow-md)' }}
      >
        <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />

        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            p: '3px 9px',
            border: `1px solid ${INK}`,
            fontFamily: HEADING,
            fontSize: '10px',
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: INK,
          }}
        >
          Observer login
        </Box>
        <Typography
          component="h1"
          sx={{ m: '10px 0 0', fontFamily: HEADING, fontSize: 26, fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase', lineHeight: 1.15 }}
        >
          Acesso de auditoria a uma conta de trial
        </Typography>
        <Typography sx={{ m: '7px 0 0', fontSize: 13, lineHeight: 1.55, color: MUTED72 }}>
          Esta sessao e gerada por um link assinado pelo backend. Use apenas para
          verificar os dados sinteticos inseridos pelo agente de IA.
        </Typography>

        <Box sx={{ mt: '18px' }}>
          {stage === 'missing-key' ? (
            <Box sx={{ p: '9px 11px', border: `1px solid ${INK}`, fontSize: 12.5, lineHeight: 1.5, color: INK }}>
              O link nao traz uma chave de acesso (<code style={{ fontFamily: 'inherit', fontWeight: 600 }}>?key=...</code>). Solicite um novo link
              via <code style={{ fontFamily: 'inherit', fontWeight: 600 }}>python manage.py issue_observer_link --email &lt;...&gt;</code>.
            </Box>
          ) : null}

          {stage === 'loading' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <CircularProgress size={26} sx={{ color: ACC }} />
              <Typography sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED58 }}>
                Validando o link e abrindo a sessao do operador...
              </Typography>
            </Box>
          ) : null}

          {stage === 'error' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <Box sx={{ p: '9px 11px', border: `1px solid ${INK}`, fontSize: 12.5, lineHeight: 1.45, color: INK }}>
                {errorMessage}
              </Box>
              <Box sx={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
                <Box component={RouterLink} to="/" sx={secondaryLinkSx}>
                  Voltar para o site
                </Box>
                <Box
                  component="a"
                  href="mailto:contato@aviatto.ai?subject=Observer%20login%20com%20problema"
                  sx={{
                    ...secondaryLinkSx,
                    border: `1px solid ${ACC}`,
                    background: ACC,
                    color: 'var(--color-bg)',
                    '&:hover': { background: 'var(--color-accent-600)', borderColor: 'var(--color-accent-600)' },
                  }}
                >
                  Falar com o suporte
                </Box>
              </Box>
            </Box>
          ) : null}

          {stage === 'success' && session ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <Box
                sx={{
                  p: '12px 13px',
                  border: `1px solid ${ACC}`,
                  background: 'color-mix(in srgb, var(--color-accent) 7%, transparent)',
                  fontSize: 13,
                  lineHeight: 1.55,
                  color: ACC8,
                }}
              >
                Sessao de observador aberta para{' '}
                <Box component="strong" sx={{ fontFamily: HEADING, fontWeight: 600 }}>
                  {session.user?.email || session.user?.username || 'usuario'}
                </Box>
                .
                {session.trial?.is_expired ? ' (a janela de trial ja expirou)' : ''}
              </Box>

              <Box sx={{ border: '1px solid var(--color-divider)' }}>
                {sessionFacts.map((fact) => (
                  <Box
                    key={fact.k}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      p: '8px 13px',
                      borderBottom: '1px dashed var(--color-neutral-200)',
                      '&:last-of-type': { borderBottom: 'none' },
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box component="span" sx={{ flexShrink: 0, width: 150, fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: MUTED58 }}>
                      {fact.k}
                    </Box>
                    <Box component="span" sx={{ flex: 1, minWidth: 140, fontFamily: HEADING, fontSize: 13, fontWeight: 600 }}>
                      {fact.v}
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box component="button" type="button" onClick={handleEnterApp} sx={primaryBtnSx}>
                Entrar no painel para auditar dados
              </Box>
              <Typography sx={{ fontSize: 11.5, lineHeight: 1.5, color: MUTED58 }}>
                Boas praticas: deslogue ao terminar a auditoria. Nao compartilhe este link.
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
